// Beistelteile-Sektion — eingebettet in jede Auftrag-Detail-Seite
// 3 Komponenten: BeistelteileSektion (Liste + Add-Button), AddModal, EingangModal
import { useState, useEffect, useCallback } from 'react';
import { Package, Plus, Clock, PackageCheck, Wrench, Truck, AlertTriangle, OctagonAlert, X, Check, Trash2 } from 'lucide-react';
import { api, type Beistelteil, type BeistelteilStatus, type BeistelteilModul } from '../lib/api';

type Props = {
  auftragModul: BeistelteilModul;
  auftragId: number;
};

const STATUS_LABEL: Record<BeistelteilStatus, string> = {
  erwartet: 'erwartet',
  eingegangen: 'eingegangen',
  verbaut: 'verbaut',
  versendet: 'versendet',
  fehlt: 'fehlt',
  beschaedigt: 'beschädigt',
};

const STATUS_CLASS: Record<BeistelteilStatus, string> = {
  erwartet:    'bg-amber-100 text-amber-800',
  eingegangen: 'bg-emerald-100 text-emerald-800',
  verbaut:     'bg-blue-100 text-blue-800',
  versendet:   'bg-purple-100 text-purple-800',
  fehlt:       'bg-red-100 text-red-800',
  beschaedigt: 'bg-orange-100 text-orange-800',
};

function StatusBadge({ status, ueberfaellig }: { status: BeistelteilStatus; ueberfaellig?: boolean }) {
  const Icon =
    status === 'erwartet' ? Clock :
    status === 'eingegangen' ? PackageCheck :
    status === 'verbaut' ? Wrench :
    status === 'versendet' ? Truck :
    status === 'fehlt' ? AlertTriangle :
    OctagonAlert;
  const cls = ueberfaellig ? 'bg-red-100 text-red-800' : STATUS_CLASS[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" />
      {ueberfaellig ? 'überfällig' : STATUS_LABEL[status]}
    </span>
  );
}

export default function BeistelteileSektion({ auftragModul, auftragId }: Props) {
  const [items, setItems] = useState<Beistelteil[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [eingangFor, setEingangFor] = useState<Beistelteil | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.beistelteile.listForAuftrag(auftragModul, auftragId);
      setItems(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [auftragModul, auftragId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!confirm('Beistelteil wirklich löschen?')) return;
    try {
      await api.beistelteile.delete(id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Fehler beim Löschen');
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-600" />
          Beistelteile {items.length > 0 && <span className="text-slate-400 font-normal">({items.length})</span>}
        </h3>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="w-3.5 h-3.5" /> Hinzufügen
        </button>
      </div>

      {loading && (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded text-sm">
          {error} <button onClick={load} className="underline ml-2">Retry</button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-slate-500 text-sm py-4 text-center">
          Keine Beistelteile erfasst.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {items.map(item => {
            const ueberfaellig = item.status === 'erwartet' &&
              item.erwartet_bis !== null && item.erwartet_bis < today;
            return (
              <li key={item.id} className="py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{item.bezeichnung}</span>
                    <StatusBadge status={item.status} ueberfaellig={ueberfaellig} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.erwartete_stueckzahl} Stk
                    {item.zeichnungs_nr && ` · ${item.zeichnungs_nr}`}
                    {item.erwartet_bis && ` · erwartet bis ${formatDate(item.erwartet_bis)}`}
                    {item.eingang_datum && ` · eingegangen ${formatDate(item.eingang_datum)}`}
                    {item.lagerplatz && ` · ${item.lagerplatz}`}
                    {item.ist_stueckzahl !== null && item.ist_stueckzahl !== item.erwartete_stueckzahl &&
                      ` · ⚠ ${item.ist_stueckzahl}/${item.erwartete_stueckzahl} Stk`}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {item.status === 'erwartet' && (
                    <button
                      onClick={() => setEingangFor(item)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                    >
                      <Check className="w-3 h-3" /> Eingang
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1 text-slate-400 hover:text-red-600"
                    title="Löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showAdd && (
        <AddModal
          auftragModul={auftragModul}
          auftragId={auftragId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}

      {eingangFor && (
        <EingangModal
          item={eingangFor}
          onClose={() => setEingangFor(null)}
          onSaved={() => { setEingangFor(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Add-Modal (Sachbearbeiter)
// ============================================================
function AddModal({ auftragModul, auftragId, onClose, onSaved }: {
  auftragModul: BeistelteilModul; auftragId: number;
  onClose: () => void; onSaved: () => void;
}) {
  const [bezeichnung, setBezeichnung] = useState('');
  const [stueck, setStueck] = useState(1);
  const [zeichnungsNr, setZeichnungsNr] = useState('');
  const [erwartetBis, setErwartetBis] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bezeichnung.trim().length < 2) {
      setError('Bezeichnung muss mind. 2 Zeichen haben');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.beistelteile.create({
        auftrag_modul: auftragModul,
        auftrag_id: auftragId,
        bezeichnung: bezeichnung.trim(),
        zeichnungs_nr: zeichnungsNr.trim() || null,
        erwartete_stueckzahl: stueck,
        erwartet_bis: erwartetBis || null,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Beistelteil hinzufügen</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Bezeichnung *</label>
            <input
              type="text"
              value={bezeichnung}
              onChange={e => setBezeichnung(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              placeholder="z.B. Welle 60mm"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Anzahl *</label>
              <input
                type="number"
                min={1}
                value={stueck}
                onChange={e => setStueck(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zeichnungs-Nr</label>
              <input
                type="text"
                value={zeichnungsNr}
                onChange={e => setZeichnungsNr(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Erwartet bis</label>
            <input
              type="date"
              value={erwartetBis}
              onChange={e => setErwartetBis(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notiz</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 p-2 rounded text-sm">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Eingang-Modal (Versand-Abteilung)
// ============================================================
function EingangModal({ item, onClose, onSaved }: {
  item: Beistelteil; onClose: () => void; onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [eingangDatum, setEingangDatum] = useState(today);
  const [istStueck, setIstStueck] = useState(item.erwartete_stueckzahl);
  const [lagerplatz, setLagerplatz] = useState('');
  const [zustand, setZustand] = useState<'ok'|'beschaedigt'|'unklar'>('ok');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.beistelteile.update(item.id, {
        status: zustand === 'beschaedigt' ? 'beschaedigt' : 'eingegangen',
        eingang_datum: eingangDatum,
        ist_stueckzahl: istStueck,
        lagerplatz: lagerplatz.trim() || null,
        zustand,
        notes: notes.trim() ? (item.notes ? `${item.notes}\n[Eingang] ${notes.trim()}` : notes.trim()) : item.notes,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Buchen');
    } finally {
      setSaving(false);
    }
  };

  const teilmenge = istStueck < item.erwartete_stueckzahl;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Eingang buchen</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-slate-50 px-3 py-2 rounded mb-4 text-sm">
          <div className="font-medium">{item.bezeichnung}</div>
          <div className="text-slate-500 text-xs">
            Erwartet: {item.erwartete_stueckzahl} Stk
            {item.erwartet_bis && ` · bis ${formatDate(item.erwartet_bis)}`}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Eingang am *</label>
              <input
                type="date"
                value={eingangDatum}
                onChange={e => setEingangDatum(e.target.value)}
                max={today}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Stück (Ist) *</label>
              <input
                type="number"
                min={1}
                value={istStueck}
                onChange={e => setIstStueck(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          {teilmenge && (
            <div className="bg-amber-50 text-amber-800 border border-amber-200 p-2 rounded text-xs">
              ⚠ Teilmenge: {item.erwartete_stueckzahl - istStueck} Stk fehlen
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Lagerplatz</label>
            <input
              type="text"
              value={lagerplatz}
              onChange={e => setLagerplatz(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              placeholder="z.B. Regal A3, Hof Container 2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zustand</label>
            <select
              value={zustand}
              onChange={e => setZustand(e.target.value as 'ok'|'beschaedigt'|'unklar')}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="ok">OK</option>
              <option value="beschaedigt">Beschädigt</option>
              <option value="unklar">Unklar</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bemerkung</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Optional"
            />
          </div>
          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 p-2 rounded text-sm">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> {saving ? 'Buche…' : 'Eingang buchen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth()+1).padStart(2, '0')}.${d.getFullYear()}`;
}
